import { ClaimStatusIndicator } from '@strata/react-status-indicators';

const { Statuses } = ClaimStatusIndicator;

export const formatCodeList = (codes) => {
    if (!codes?.length) {
        return null;
    }

    const formattedCodes = codes.filter(Boolean);
    return formattedCodes.length ? formattedCodes.join(', ') : null;
};

export const formatClaimRejectCodes = (claim) => formatCodeList(claim?.rejectCodes);

/** Soften PBM all-caps reject messages for display; leave mixed-case alone. */
export const formatRejectMessage = (message) => {
    if (!message) {
        return message;
    }

    const letters = message.replace(/[^A-Za-z]/g, '');
    if (!letters || letters !== letters.toUpperCase()) {
        return message;
    }

    return `${message.charAt(0).toUpperCase()}${message.slice(1).toLowerCase()}`;
};

/**
 * Tooltip rows for reject codes / free-text messages.
 * Test claims: Mirth {code, message} pairs (+ leftover code-less rejectMessages).
 * Payer claims: pair rejectCodes[i] with rejectMessages[i] (message_1..3).
 */
export const getRejectCodeTooltipDetails = (claim) => {
    const withFormattedMessages = (rows) =>
        rows.map(({ code, message }) => ({
            code,
            message: message ? formatRejectMessage(message) : message,
        }));

    if (claim.testRejectCodes?.length) {
        const paired = claim.testRejectCodes.filter(({ code }) => code);
        const pairedMessages = new Set(paired.map(({ message }) => message).filter(Boolean));
        const leftover = (claim.rejectMessages ?? [])
            .filter(Boolean)
            .filter((message) => !pairedMessages.has(message))
            .map((message) => ({ message }));
        return withFormattedMessages([...paired, ...leftover]);
    }

    const codes = (claim.rejectCodes ?? []).filter(Boolean);
    const messages = (claim.rejectMessages ?? []).filter(Boolean);
    const length = Math.max(codes.length, messages.length);

    return withFormattedMessages(
        Array.from({ length }, (_, index) => ({
            code: codes[index],
            message: messages[index],
        })).filter(({ code, message }) => code || message),
    );
};

export const formatDrugDescription = ({
    drugName,
    drugDosageForm,
    drugStrength,
    productDescription,
}) => {
    const payerDrugLabel = [drugName, drugDosageForm, drugStrength].filter(Boolean).join(' · ');
    if (payerDrugLabel && productDescription && payerDrugLabel !== productDescription) {
        return `${payerDrugLabel} (${productDescription})`;
    }
    return payerDrugLabel || productDescription || null;
};

export const ClaimInsightClaimTypes = {
    PAID: 'paid',
    REVERSED: 'reversed',
    REJECTED: 'rejected',
    TEST: 'test',
};

export const ClaimInsightProductTypes = {
    BRAND: 'brand',
    GENERIC: 'generic',
};

export const getClaimSupplierNpiForFilter = (claim) =>
    claim.billingSupplierNpi ?? claim.assignedSupplierNpi;

export const getClaimInsightClaimType = (claim) => {
    if (claim.isTestClaim) {
        return ClaimInsightClaimTypes.TEST;
    }
    if (claim.claimStatus === Statuses.REVERSED) {
        return ClaimInsightClaimTypes.REVERSED;
    }
    if (claim.claimStatus === Statuses.REJECTED) {
        return ClaimInsightClaimTypes.REJECTED;
    }
    // PAID / CLOSED both mean paid. OUTSIDE is claim-api's default for paid fills
    // with no referral FMH status (chip shows "Filled"); include it in Paid filter.
    // INCORRECT is a paid claim submitted by the wrong pharmacy (chip shows "Incorrect").
    if (
        claim.claimStatus === Statuses.PAID ||
        claim.claimStatus === Statuses.CLOSED ||
        claim.claimStatus === Statuses.OUTSIDE ||
        claim.claimStatus === Statuses.INCORRECT
    ) {
        return ClaimInsightClaimTypes.PAID;
    }

    return null;
};

export const getClaimInsightProductType = (claim) => {
    if (claim.isBrand === true) {
        return ClaimInsightProductTypes.BRAND;
    }
    if (claim.isBrand === false) {
        return ClaimInsightProductTypes.GENERIC;
    }
    if (claim.genericIndicator === true) {
        return ClaimInsightProductTypes.GENERIC;
    }
    if (claim.genericIndicator === false) {
        return ClaimInsightProductTypes.BRAND;
    }

    return null;
};

export const enrichClaimForGridFilters = (claim) => ({
    ...claim,
    claimType: getClaimInsightClaimType(claim),
    productType: getClaimInsightProductType(claim),
    supplierNpi: getClaimSupplierNpiForFilter(claim),
});

export const enrichClaimSupplierNames = (claim, supplierMap = {}) => ({
    ...claim,
    assignedSupplierName:
        claim.assignedSupplierName ?? supplierMap[claim.assignedSupplierNpi]?.name,
    billingSupplierName: claim.billingSupplierName ?? supplierMap[claim.billingSupplierNpi]?.name,
});

const CLAIM_TYPE_FILTER_VALUES = [
    { value: ClaimInsightClaimTypes.PAID, text: 'Paid' },
    { value: ClaimInsightClaimTypes.REJECTED, text: 'Rejected' },
    { value: ClaimInsightClaimTypes.TEST, text: 'FMH Test Claim' },
    { value: ClaimInsightClaimTypes.REVERSED, text: 'Reversed' },
];

/** Default Claim Type selection: everything except FMH test claims. */
export const DEFAULT_CLAIM_TYPE_FILTERS = [
    ClaimInsightClaimTypes.PAID,
    ClaimInsightClaimTypes.REJECTED,
    ClaimInsightClaimTypes.REVERSED,
];

const PRODUCT_TYPE_FILTER_VALUES = [
    { value: ClaimInsightProductTypes.BRAND, text: 'Brand' },
    { value: ClaimInsightProductTypes.GENERIC, text: 'Generic' },
];

export const buildClaimInsightsFilterCategories = (claims = []) => {
    const categories = [
        {
            key: 'claimType',
            title: 'Claim Type',
            customValues: CLAIM_TYPE_FILTER_VALUES,
        },
        {
            key: 'productType',
            title: 'Product',
            customValues: PRODUCT_TYPE_FILTER_VALUES,
        },
    ];

    if (claims.some(({ supplierNpi }) => supplierNpi)) {
        categories.push({
            key: 'supplierNpi',
            title: 'Supplier NPI',
            showCounts: true,
        });
    }

    return categories;
};

export const getClaimProductLabel = (claim, productServiceMap) =>
    productServiceMap?.[claim.productServiceOid] ??
    formatDrugDescription(claim) ??
    claim.drugName ??
    claim.productServiceOid;

export const mapClaimInsightsFields = (claim) => ({
    memberId: claim.member_id,
    patientFirstName: claim.patient_first_name,
    patientLastName: claim.patient_last_name,
    patientDob: claim.patient_dob,
    subClarificationCode: claim.sub_clarification_code,
    claimCost: claim.claim_cost,
    adjudicationDate: claim.adjudication_date,
    drugName: claim.drug_name,
    drugDosageForm: claim.drug_dosage_form,
    drugStrength: claim.drug_strength,
    productDescription: claim.product_description,
    dispenseAsWrittenCode: claim.dispense_as_written_code,
    drugBrandGenericCode: claim.drug_brand_generic_code,
    genericIndicator: claim.generic_indicator,
    claimOverrideCodes: claim.claim_override_codes ?? [],
    usualAndCustomary: claim.usual_and_customary,
    submittedCost: claim.submitted_cost,
    drugIngredientCost: claim.drug_ingredient_cost,
    priceType: claim.price_type,
    rejectCodes: claim.reject_codes ?? [],
    rejectMessages: claim.reject_messages ?? [],
    prescriberNpi: claim.prescriber_npi,
    diagnosisCode: claim.diagnosis_code ?? claim.diagnosis_codes?.[0],
    diagnosisCodes: claim.diagnosis_codes?.length
        ? claim.diagnosis_codes.filter(Boolean)
        : [claim.diagnosis_code].filter(Boolean),
    isContingentTherapy: claim.is_contingent_therapy,
});
